import React from 'react';
import TestResultDisplay from '../../common/TestResultDisplay';

// 测试项目接口
interface TestItem {
  name: string;
  passed: boolean;
  userAnswer?: string;
  correctAnswer?: string;
  details?: string;
}

// 测试组接口
interface TestGroup {
  title: string;
  subtitle?: string;
  score?: { current: number; total: number };
  items: TestItem[];
  passed: boolean;
  note?: string;
  hasLogicalError?: boolean;
  errorReason?: string;
}

interface PsychologicalSummaryDisplayProps {
  responses: any;
  psychologicalStatus?: string;
  className?: string;
}

const PsychologicalSummaryDisplay: React.FC<PsychologicalSummaryDisplayProps> = ({ 
  responses, 
  psychologicalStatus,
  className = '' 
}) => {

  // 判断心理健康是否通过 - 基于传入的状态
  const psychologicalPassed = psychologicalStatus === 'PASS';
  
  // 从真实的responses数据中获取心理健康评估数据
  const getRealPsychologicalData = () => {
    
    // 方式1: 从直接字段获取
    if (responses?.depressedMood !== undefined || responses?.anhedonia !== undefined) {
      return {
        depressedMood: responses.depressedMood,
        anhedonia: responses.anhedonia,
        hasDepressedMood: responses.hasDepressedMood,
        hasAnhedonia: responses.hasAnhedonia
      };
    }
    
    // 方式2: 从 testResults 中获取 rawUserAnswers，合并所有心理健康测试数据
    if (responses?.testResults) {
      responses.testResults.forEach((t: any, index: number) => {
      });
      
      const psychologicalTests = responses.testResults.filter((test: any) => 
        test.name === "抑郁情绪评估" || test.name === "兴趣缺失评估" || 
        test.name === "心理健康筛查" || test.name === "PHQ-2筛查"
      );
      
      if (psychologicalTests.length > 0) {
        // 合并所有心理健康测试的rawUserAnswers数据
        const combinedData: any = {};
        psychologicalTests.forEach((test: any) => {
          if (test.rawUserAnswers) {
            // 使用 Object.assign 确保完整合并
            Object.assign(combinedData, test.rawUserAnswers);
          }
        });
        
        if (Object.keys(combinedData).length > 0) {
          return combinedData;
        }
      }
    }
    
    // 方式3: 从domainResult中的testResults获取
    if ((responses as any)?.status && (responses as any)?.testResults) {
      const psychologicalTests = (responses as any).testResults.filter((test: any) => 
        test.name === "抑郁情绪评估" || test.name === "兴趣缺失评估" || 
        test.name === "心理健康筛查" || test.name === "PHQ-2筛查"
      );
      
      if (psychologicalTests.length > 0) {
        // 合并所有心理健康测试的rawUserAnswers数据
        const combinedData: any = {};
        psychologicalTests.forEach((test: any) => {
          if (test.rawUserAnswers) {
            // 使用 Object.assign 确保完整合并
            Object.assign(combinedData, test.rawUserAnswers);
          }
        });
        
        if (Object.keys(combinedData).length > 0) {
          return combinedData;
        }
      }
    }
    
    return null;
  };
  
  // 动态生成心理健康测试数据，基于真实数据
  const generatePsychologicalTestData = (): TestGroup[] => {
    const realData = getRealPsychologicalData();
    
    // 🔍 增强调试：详细显示获取到的心理健康数据
    if (realData) {
    }
    
    // 如果没有真实数据，显示基础状态信息
    if (!realData) {
      return [
        {
          title: '心理健康状态评估',
          passed: psychologicalPassed,
          items: [
            {
              name: '评估结果',
              passed: psychologicalPassed,
              userAnswer: psychologicalPassed ? '心理状态正常' : '心理状态需要关注',
              details: '基于WHO ICOPE心理健康评估标准'
            }
          ]
        }
      ];
    }

    const testGroups: TestGroup[] = [];
    
    // 判断两个核心问题的回答
    const hasDepressedMood = realData.depressedMood === true || realData.hasDepressedMood === true;
    const hasAnhedonia = realData.anhedonia === true || realData.hasAnhedonia === true;
    
    // 抑郁情绪筛查 - 基于真实数据
    if (realData.depressedMood !== undefined || realData.hasDepressedMood !== undefined) {
      testGroups.push({
        title: '抑郁情绪筛查',
        passed: !hasDepressedMood,
        items: [
          {
            name: '抑郁情绪问题',
            passed: !hasDepressedMood,
            userAnswer: hasDepressedMood ? '是的，我有这些困扰' : '没有，我没有这些问题',
            details: '问题：过去两周，您是否因感到情绪低落、沮丧或绝望而受到困扰？'
          }
        ]
      });
    }
    
    // 兴趣缺失筛查 - 基于真实数据
    if (realData.anhedonia !== undefined || realData.hasAnhedonia !== undefined) {
      testGroups.push({
        title: '兴趣缺失筛查',
        passed: !hasAnhedonia,
        items: [
          {
            name: '兴趣缺失问题',
            passed: !hasAnhedonia,
            userAnswer: hasAnhedonia ? '是的，我有这些困扰' : '没有，我没有这些问题',
            details: '问题：过去两周，您是否因对事物几乎没有兴趣或乐趣而受到困扰？'
          }
        ]
      });
    }

    // 如果没有任何真实测试数据，显示状态信息
    if (testGroups.length === 0) {
      testGroups.push({
        title: '心理健康状态评估',
        passed: psychologicalPassed,
        items: [
          {
            name: '评估结果',
            passed: psychologicalPassed,
            userAnswer: psychologicalPassed ? '心理状态正常' : '心理状态需要关注',
            details: '基于WHO ICOPE心理健康评估标准'
          }
        ]
      });
    }

    return testGroups;
  };

  const testGroups = generatePsychologicalTestData();
  const passedCount = testGroups.filter(group => group.passed).length;
  const totalCount = testGroups.length;

  return (
    <TestResultDisplay
      title="心理健康评估结果"
      subtitle={`综合评估: ${passedCount}/${totalCount}项指标正常`}
      testGroups={testGroups}
      className={className}
    />
  );
};

export default PsychologicalSummaryDisplay;